import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Card, CardContent, Grid, CircularProgress, Chip, Stack } from '@mui/material';
import { EmojiEvents, EventNote, AttachMoney, ArrowForward, LoginOutlined, HowToVote } from '@mui/icons-material';
import { listarConcursosAbertos } from '../../api/concursos';
import { obterVotoPopular } from '../../api/votoPopular';
import useTenant from '../../hooks/useTenant';
import FundoMarca from '../../components/FundoMarca';
import { Trans, useTranslation } from 'react-i18next';
import { formatarMoeda, formatarPeriodo } from '../../utils/formatacao';

const HZERO_LIME = '#CCFF00';

// Página pública do tenant: concursos com inscrições abertas + atalhos de login/cadastro.
function LandingPage() {
  const navigate = useNavigate();
  const { branding, selectedSlug } = useTenant();
  const { t } = useTranslation();
  const [concursos, setConcursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votoPopularAberto, setVotoPopularAberto] = useState(false);

  const nomeTenant = branding?.nome || 'Konq';
  const logoUrl = branding?.logoUrl || null;
  const accent = branding?.corDestaque || branding?.corSecundaria || HZERO_LIME;

  useEffect(() => {
    if (!selectedSlug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    listarConcursosAbertos()
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setConcursos(Array.isArray(data) ? data : []);
      })
      .catch(() => setConcursos([]))
      .finally(() => setLoading(false));
  }, [selectedSlug]);

  // Link pro voto popular só enquanto a votação está aberta
  useEffect(() => {
    if (!selectedSlug) return;
    obterVotoPopular()
      .then((res) => setVotoPopularAberto((res.data?.data?.votacoes ?? []).some((v) => v.estado === 'Aberta')))
      .catch(() => setVotoPopularAberto(false));
  }, [selectedSlug]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Helmet title={t('landing.inscricoes')} />

      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 6 },
          py: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(6px)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={nomeTenant}
            sx={{ height: { xs: 30, sm: 36 }, maxWidth: { xs: 120, sm: 180 }, minWidth: 0, objectFit: 'contain' }}
          />
        ) : (
          <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.5 }}>{nomeTenant}</Typography>
        )}
        {/* No celular o logo encolhe e o "Entrar" fica sem ícone, pra caber em 360px */}
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            color="inherit"
            startIcon={<LoginOutlined />}
            onClick={() => navigate('/login')}
            sx={{ '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inherit' } } }}
          >
            {t('landing.entrar')}
          </Button>
          <Button variant="contained" onClick={() => navigate('/registre-se')}>
            {t('landing.cadastrar')}
          </Button>
        </Stack>
      </Box>

      {/* Hero */}
      <Box sx={{ position: 'relative', overflow: 'hidden', color: '#fff' }}>
        <FundoMarca direcao="esquerda" />
        <Box sx={{ position: 'relative', px: { xs: 3, md: 6 }, py: { xs: 6, md: 10 }, maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ width: 40, height: 3, bgcolor: accent, borderRadius: 1, mb: 3 }} />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: 1.15,
              letterSpacing: -1,
              mb: 2,
              maxWidth: 640,
            }}
          >
            <Trans i18nKey="landing.hero.titulo" components={{ br: <br /> }} />
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.7)',
              maxWidth: 520,
              lineHeight: 1.7,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
            }}
          >
            {t('landing.hero.subtitulo')}
          </Typography>
          {votoPopularAberto && (
            <Button
              variant="contained"
              size="large"
              startIcon={<HowToVote />}
              onClick={() => navigate('/voto-popular')}
              sx={{ 'mt': 4, 'bgcolor': '#fff', 'color': '#111', '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' } }}
            >
              {t('Popular vote is open — vote for your favorite')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Concursos abertos */}
      <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: { xs: 4, md: 6 }, maxWidth: 1200, width: '100%', mx: 'auto' }}>
        {/* Sem concurso aberto o título contradiz o aviso de "encerradas" — some */}
        {(loading || concursos.length > 0) && (
          <Typography variant="h5" sx={{ mb: 3 }}>
            {t('landing.inscricoesAbertas')}
          </Typography>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : concursos.length === 0 ? (
          // Aviso centralizado na página (sem título acima), na largura toda no celular
          <Card sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <EmojiEvents sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="h6" gutterBottom>
                {t('landing.encerradas.titulo')}
              </Typography>
              <Typography variant="body2">{t('landing.encerradas.texto')}</Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {concursos.map((concurso) => (
              <Grid item xs={12} sm={6} lg={4} key={concurso.concursoId ?? concurso.id}>
                <ConcursoCard concurso={concurso} onParticipar={() => navigate('/registre-se')} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Rodapé */}
      <Box
        sx={{
          px: { xs: 2, md: 6 },
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.disabled">
            © {new Date().getFullYear()} Konq Platform — by
          </Typography>
          <Box component="img" src="/assets/logo-hzero.png" alt="hzero" sx={{ height: 10, opacity: 0.6 }} />
        </Box>
        <Typography variant="caption" color="text.disabled">
          {t('landing.privacidade')}
        </Typography>
      </Box>
    </Box>
  );
}

function InfoLinha({ icon, rotulo, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          'width': 32,
          'height': 32,
          'borderRadius': 2,
          'flexShrink': 0,
          'display': 'flex',
          'alignItems': 'center',
          'justifyContent': 'center',
          'bgcolor': 'grey.100',
          'color': 'text.secondary',
          '& svg': { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
          {rotulo}
        </Typography>
        {children}
      </Box>
    </Stack>
  );
}

function ConcursoCard({ concurso, onParticipar }) {
  const { t } = useTranslation();
  const taxa = concurso.taxaInscricao ?? concurso.taxa ?? null;
  const gratuito = !taxa || taxa === 0;
  const periodo = formatarPeriodo(concurso.periodoInicio, concurso.periodoFim);
  const periodoConcurso = formatarPeriodo(concurso.dataInicio, concurso.dataFim);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <EmojiEvents />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              {concurso.nome ?? t('Contest')}
            </Typography>
            {concurso.periodoNome && (
              <Chip label={concurso.periodoNome} size="small" sx={{ mt: 0.5, fontWeight: 600 }} />
            )}
          </Box>
        </Stack>

        <Stack spacing={1.5} sx={{ flex: 1 }}>
          {periodo && (
            <InfoLinha icon={<EventNote />} rotulo={t('landing.card.periodoInscricoes')}>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                {periodo}
              </Typography>
            </InfoLinha>
          )}
          {periodoConcurso && (
            <InfoLinha icon={<EmojiEvents />} rotulo={t('landing.card.dataEvento')}>
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                {periodoConcurso}
              </Typography>
            </InfoLinha>
          )}
          <InfoLinha icon={<AttachMoney />} rotulo={t('landing.card.taxa')}>
            {gratuito ? (
              <Typography variant="body2" color="success.main" fontWeight={700}>
                {t('landing.card.gratuito')}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.primary" fontWeight={600}>
                {formatarMoeda(taxa)}
              </Typography>
            )}
          </InfoLinha>
        </Stack>

        <Button variant="contained" size="large" fullWidth endIcon={<ArrowForward />} onClick={onParticipar}>
          {t('landing.card.participar')}
        </Button>
      </CardContent>
    </Card>
  );
}

export default LandingPage;
